
import React from 'react';
import { Share2, Crown, Command } from 'lucide-react';
import { saveSelectionToSupabase } from '@/lib/supabase';
import logo from '/lovable-uploads/40a4ee59-7714-4091-a6f7-e5ba5819fe5f.png';
import PageLayout from '@/components/PageLayout';
import AdBanner from '@/components/AdBanner';
import { useTranslation } from 'react-i18next';
import { useOnboardingContext } from '@/context/OnboardingContext';
import { formatUssdTelUrl } from '@/utils/ussd-utils';

const Combo = () => {
  const { t } = useTranslation();
  const { userData } = useOnboardingContext();

  const handleComboSelect = async (comboName: string, price: string, ussdCode: string) => {
    // Create selection object
    const selection = {
      type: 'Combo',
      name: comboName,
      price: price,
      dateTime: new Date().toISOString(),
    };
    
    // Save to localStorage
    try {
      const existingSelections = JSON.parse(localStorage.getItem('selections') || '[]');
      existingSelections.push(selection);
      localStorage.setItem('selections', JSON.stringify(existingSelections));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
    
    // Save to Supabase
    try {
      await saveSelectionToSupabase(selection, userData?.email);
    } catch (error) {
      console.error('Error in Supabase save:', error);
    }
    
    // Redirect to phone dialer with encoded USSD
    window.location.href = formatUssdTelUrl(ussdCode);
  };

  return (
    <PageLayout 
      title="Combo" 
      logo={logo}
      showBackButton
      colorScheme="bg-gradient-to-r from-purple-600 via-violet-600 to-indigo-600"
    >
      {/* Ad Banner for combo page */}
      <AdBanner pageSlug="combo" />
      
      {/* Combo Items */}
      <div className="space-y-2 rounded-xl overflow-hidden shadow-lg">
        {/* Classic Combo */}
        <div 
          className="bg-white border-b cursor-pointer hover:bg-purple-50 transition-colors"
          onClick={() => handleComboSelect("Classic", "500", "*164#1*1*1#")}
        >
          <div className="flex items-start p-4">
            <div className="mr-4 rounded-full bg-gradient-to-br from-pink-400 to-pink-600 p-4 flex items-center justify-center shadow-md">
              <Share2 className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <h2 className="text-xl font-medium text-gray-700">Classic</h2>
                <div className="flex items-end">
                  <span className="text-4xl font-bold text-gray-700">500</span>
                  <span className="text-lg ml-1 text-gray-500">DJF</span>
                </div>
              </div>
              <p className="text-gray-500 mt-1">{t('combo.classic.description')}</p>
              <div className="flex items-center mt-2">
                <span className="text-gray-500">{t('combo.validity')}</span>
                <span className="mx-4 text-2xl font-medium text-gray-700">30</span>
                <span className="text-gray-500">{t('data.days')}</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Medium Combo */}
        <div 
          className="bg-white border-b cursor-pointer hover:bg-purple-50 transition-colors"
          onClick={() => handleComboSelect("Medium", "1000", "*164#1*2*1#")}
        >
          <div className="flex items-start p-4">
            <div className="mr-4 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 p-4 flex items-center justify-center shadow-md">
              <Crown className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <h2 className="text-xl font-medium text-gray-700">Medium</h2>
                <div className="flex items-end">
                  <span className="text-4xl font-bold text-gray-700">1000</span>
                  <span className="text-lg ml-1 text-gray-500">DJF</span>
                </div>
              </div>
              <p className="text-gray-500 mt-1">{t('combo.medium.description')}</p>
              <div className="flex items-center mt-2">
                <span className="text-gray-500">{t('combo.validity')}</span>
                <span className="mx-4 text-2xl font-medium text-gray-700">30</span>
                <span className="text-gray-500">{t('data.days')}</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Premium Combo */}
        <div 
          className="bg-white border-b cursor-pointer hover:bg-purple-50 transition-colors"
          onClick={() => handleComboSelect("Premium", "2000", "*164#1*3*1#")}
        >
          <div className="flex items-start p-4">
            <div className="mr-4 rounded-full bg-gradient-to-br from-amber-700 to-yellow-600 p-4 flex items-center justify-center shadow-md">
              <Command className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <h2 className="text-xl font-medium text-gray-700">Premium</h2>
                <div className="flex items-end">
                  <span className="text-4xl font-bold text-gray-700">2000</span>
                  <span className="text-lg ml-1 text-gray-500">DJF</span>
                </div>
              </div>
              <p className="text-gray-500 mt-1">{t('combo.premium.description')}</p>
              <div className="flex items-center mt-2">
                <span className="text-gray-500">{t('combo.validity')}</span>
                <span className="mx-4 text-2xl font-medium text-gray-700">30</span>
                <span className="text-gray-500">{t('data.days')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Combo;
